import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TeacherLayout from '../../components/layout/TeacherLayout';
import StudentLayout from '../../components/layout/StudentLayout';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Paper, 
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { VpnKey as VpnKeyIcon, ArrowBack } from '@mui/icons-material';
import { authAPI } from '../../services/api';

const ChangePassword = () => {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Determine which layout to use based on user role
  const isStudent = user?.isStudent || user?.is_student || (user?.role_name && user?.role_name.toString().toLowerCase() === 'student');
  const LayoutComponent = isStudent ? StudentLayout : TeacherLayout;

  const validateField = (field, value) => {
    const newErrors = { ...errors };
    
    switch (field) {
      case 'currentPassword':
        if (!value) {
          newErrors.currentPassword = '';
        } else {
          newErrors.currentPassword = '';
        }
        break;
      case 'newPassword':
        if (!value) {
          newErrors.newPassword = '';
        } else if (value.length < 8) {
          newErrors.newPassword = 'Password must be at least 8 characters long';
        } else {
          newErrors.newPassword = '';
        }
        // Also validate confirm password if it has a value
        if (confirmPassword && confirmPassword !== value) {
          newErrors.confirmPassword = 'Passwords do not match';
        } else if (confirmPassword) {
          newErrors.confirmPassword = '';
        }
        break;
      case 'confirmPassword':
        if (!value) {
          newErrors.confirmPassword = '';
        } else if (value !== newPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        } else {
          newErrors.confirmPassword = '';
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setErrors({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    
    // Validate all fields
    let hasErrors = false;
    const validationErrors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    
    if (!currentPassword) {
      validationErrors.currentPassword = 'Current password is required';
      hasErrors = true;
    }
    
    if (!newPassword) {
      validationErrors.newPassword = 'New password is required';
      hasErrors = true;
    } else if (newPassword.length < 8) {
      validationErrors.newPassword = 'Password must be at least 8 characters long';
      hasErrors = true;
    }
    
    if (!confirmPassword) {
      validationErrors.confirmPassword = 'Please confirm your new password';
      hasErrors = true;
    } else if (newPassword !== confirmPassword) {
      validationErrors.confirmPassword = 'Passwords do not match';
      hasErrors = true;
    }
    
    if (hasErrors) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      setSuccess('');
      
      const response = await authAPI.changePassword({
        oldPassword: currentPassword,
        newPassword: newPassword,
        confirmPassword: confirmPassword
      });
      
      setSuccess(response.data?.message || 'Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setErrors({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Redirect to login after successful password change
      setTimeout(() => {
        localStorage.removeItem('token');
        navigate('/login', { 
          state: { message: 'Password changed successfully. Please log in with your new password.' } 
        });
      }, 2000);
      
    } catch (err) {
      console.error('Change password error:', err);
      console.error('Error response data:', err.response?.data);
      
      // Handle specific error cases
      const errorMessages = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
      
      if (err.response?.status === 400) {
        if (err.response.data?.old_password) {
          errorMessages.currentPassword = err.response.data.old_password[0];
        } else if (err.response.data?.new_password) {
          errorMessages.newPassword = err.response.data.new_password[0];
        } else if (err.response.data?.message) {
          // Check if it's about old/new password requirement
          if (err.response.data.message.includes('old') || err.response.data.message.includes('new')) {
            if (err.response.data.message.includes('old')) {
              errorMessages.currentPassword = err.response.data.message;
            }
            if (err.response.data.message.includes('new')) {
              errorMessages.newPassword = err.response.data.message;
            }
          } else {
            errorMessages.newPassword = err.response.data.message;
          }
        } else {
          errorMessages.newPassword = 'Invalid request. Please check your input and try again.';
        }
      } else if (err.response?.status === 401) {
        errorMessages.newPassword = 'Your session has expired. Please log in again.';
        setTimeout(() => {
          localStorage.removeItem('token');
          navigate('/login');
        }, 3000);
      } else if (err.response?.status === 500) {
        errorMessages.newPassword = 'A server error occurred. Please try again later.';
      } else if (!err.response) {
        errorMessages.newPassword = 'Network error. Please check your connection and try again.';
      } else {
        errorMessages.newPassword = err.response?.data?.message || 'Failed to change password. Please try again.';
      }
      
      setErrors(errorMessages);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LayoutComponent>
      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 4, position: 'relative' }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ position: 'absolute', left: 20, top: 20, zIndex: 1 }}
          >
            Back
          </Button>
          
          <Box sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Change Password
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center">
              Enter your current password and choose a new password
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Current Password"
                type="password"
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  validateField('currentPassword', e.target.value);
                }}
                fullWidth
                required
                error={!!errors.currentPassword}
                helperText={errors.currentPassword}
              />
              
              <TextField
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  validateField('newPassword', e.target.value);
                }}
                fullWidth
                required
                error={!!errors.newPassword}
                helperText={errors.newPassword || (newPassword && newPassword.length < 8 ? 'Password must be at least 8 characters long' : '')}
              />
              
              <TextField
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  validateField('confirmPassword', e.target.value);
                }}
                fullWidth
                required
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
              />
              
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <VpnKeyIcon />}
                sx={{ mt: 2 }}
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </LayoutComponent>
  );
};

export default ChangePassword;
